import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

export function useI18n() {
  const { i18n } = useTranslation();
  const locale = i18n.language || "en-US";
  // Check if i18n is initialized and dir method exists before calling it
  const dir =
    i18n.isInitialized && typeof i18n.dir === "function" ? i18n.dir(locale) : "ltr";
  return { dir, locale, i18n };
}

export type TFunction = ReturnType<typeof useT>;
export type TFunctionArgs = Parameters<TFunction>;

// TODO: should allow null values and edit components instead
export function useT(
  namespace?: string,
): (key: string, defaultValue?: string, options?: any) => string {
  const { i18n } = useI18n();

  // Load the namespace if it hasn't been loaded yet
  useEffect(() => {
    if (
      namespace &&
      i18n.isInitialized &&
      typeof i18n.hasLoadedNamespace === "function" &&
      !i18n.hasLoadedNamespace(namespace) &&
      typeof i18n.loadNamespaces === "function"
    ) {
      i18n.loadNamespaces(namespace).catch(() => {
        // Failed to load translation namespace
      });
    }
  }, [i18n, i18n.isInitialized, namespace]);

  // Memoized so consumers can safely use the returned function in effect/callback
  // dependency arrays without retriggering on every render. Init/fallback checks
  // happen at call time; i18n lookups are live, so a memoized wrapper stays correct
  // once resources load. Identity only changes when the language (or namespace)
  // changes, which is exactly when dependents should re-run.
  return useCallback(
    (key: string, defaultValue?: string, options?: any): string => {
      // Safety check: if i18n is not initialized, fall back to defaultValue/key
      if (!i18n.isInitialized) {
        return defaultValue || key;
      }

      const t = i18n.getFixedT(i18n.language, namespace);
      if (!t) {
        return defaultValue || key;
      }

      let result: any;
      if (defaultValue !== undefined) {
        result = t(key, defaultValue, options);
      } else if (options !== undefined) {
        result = t(key, options);
      } else {
        result = t(key);
      }
      // Convert result to string, handling null/undefined cases.
      // i18next treats an existing "" as a successful lookup and ignores
      // defaultValue. A blank catalog entry should use the caller fallback
      // (for example USER_MSG.ERROR_DXF_FAILED) instead of rendering nothing.
      if (result == null || (result === "" && defaultValue)) {
        return defaultValue ?? key;
      }
      return String(result);
    },
    [i18n, i18n.isInitialized, i18n.language, namespace],
  );
}
